<template>
  <StepLayout
    :data-test="`group-${step.id}`"
    :tracking="trackingStep"
    :without-left-template
  >
    <template #left>
      <div class="hidden md:block" :class="[desktopGuidanceClasses]">
        <slot :illustration-classes :name="`guidance-${step.id}`" />
      </div>

      <fieldset
        class="flex md:hidden justify-between"
        :class="{ 'mb-16': step.id !== 'grades' }"
      >
        <legend class="heading-1 contents">
          <span v-if="isTitleWithoutEmphasis">
            {{ step.title }}
          </span>
          <FormattedMessage v-else :definition="step.title as I18nDefinition">
            <template #price>
              <span v-if="step.titlePrice" class="bg-static-info-max">
                {{ i18n.price(step.titlePrice) }}
              </span>
            </template>
            <template #emphasis>
              {{
                typeof step.titleEmphasis === 'string' || !step.titleEmphasis
                  ? step.titleEmphasis
                  : i18n(step.titleEmphasis)
              }}
            </template>
          </FormattedMessage>
        </legend>

        <slot :name="`title-${step.id}`" />
      </fieldset>
      <div>
        <slot :name="`description-${step.id}`" />
      </div>

      <StepIllustration
        v-if="step.desktopIllustration"
        class="mb-24"
        :class="[illustrationClasses]"
        :position="step.desktopIllustration.position"
        :src="step.desktopIllustration.src"
        :with-example-image
      />
    </template>

    <template #right>
      <fieldset>
        <div class="mb-12 hidden items-baseline justify-between md:flex">
          <legend :id="`heading-${step.id}`" class="heading-1">
            <span v-if="isTitleWithoutEmphasis">
              {{ step.title }}
            </span>
            <FormattedMessage v-else :definition="step.title as I18nDefinition">
              <template #price>
                <span v-if="step.titlePrice" class="bg-static-info-max">
                  {{ i18n.price(step.titlePrice) }}
                </span>
              </template>
              <template #emphasis>
                {{
                  typeof step.titleEmphasis === 'string' || !step.titleEmphasis
                    ? step.titleEmphasis
                    : i18n(step.titleEmphasis)
                }}
              </template>
            </FormattedMessage>
          </legend>

          <slot :name="`title-${step.id}`" />
        </div>

        <div class="relative block md:hidden">
          <slot :illustration-classes :name="`guidance-${step.id}`" />
        </div>

        <CardGuidance
          v-if="step.guidance && !hideGuidanceCard"
          v-bind="{ ...step.guidance, customOpenModalTrigger }"
          :class="{ 'hidden md:flex': GRADES_STEPS.includes(step.id) }"
        />

        <ul
          :aria-labelledby="`heading-${step.id}`"
          class="list-none"
          :class="stepOptionsClasses(step)"
          :data-qa="`heading-${step.id}`"
          role="radiogroup"
        >
          <StepOption
            v-for="(option, index) in options"
            :key="option.trackingValue"
            :first-picker-id
            :hide-sold-out
            :index
            :is-group-loading
            :is-small="isSmallPicker"
            :option
            :step
            @changed="onOptionChanged"
            @click="onOptionClick"
          />

          <li v-if="shouldDisplayToggleButton">
            <LargePicker
              :aria-expanded="displayAllItems"
              class="m-auto h-full"
              :index="options.length"
              :is-small="isSmallPicker"
              :label="toggleLabel"
              @click="toggleDisplayAllItems"
            />
          </li>
        </ul>

        <slot :name="`step-end-${step.id}`" />
      </fieldset>
    </template>
  </StepLayout>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { tw } from '@backmarket/utils/string/tw'

import { GRADES_STEPS } from '../../CustomizationFunnel.constants'
import type { Step } from '../../utils/types'
import CardGuidance from '../CardGuidance/CardGuidance.vue'
import LargePicker from '../LargePicker/LargePicker.vue'
import StepIllustration from '../StepIllustration/StepIllustration.vue'
import StepLayout from '../StepLayout/StepLayout.vue'
import StepOption from '../StepOption/StepOption.vue'

import translations from './Step.translations'

const MAX_PICKER_ITEMS = 6

const props = withDefaults(
  defineProps<{
    step: Step
    pickers: GetPickersResponse
    product: GetProductResponse
    withoutLeftTemplate?: boolean
    customOpenModalTrigger?: () => void
    hideGuidanceCard?: boolean
    hideSoldOut?: boolean
    desktopGuidanceClasses?: string
    illustrationClasses?: string | string[]
    withExampleImage?: boolean
    firstPickerId?: string
  }>(),
  {
    hideSoldOut: false,
    hideGuidanceCard: false,
    withoutLeftTemplate: false,
    customOpenModalTrigger: undefined,
    desktopGuidanceClasses: '',
    illustrationClasses: undefined,
    withExampleImage: false,
    firstPickerId: '',
  },
)

defineSlots<
  Record<`guidance-${string}`, { illustrationClasses?: string[] | undefined }> &
    Record<`title-${string}`, unknown> &
    Record<`description-${string}`, unknown> &
    Record<`step-end-${string}`, unknown>
>()

const displayAllItems = ref(false)
const isGroupLoading = ref(false)
const i18n = useI18n()

const shouldDisplayToggleButton = computed(() => {
  return props.step.options.length > MAX_PICKER_ITEMS
})

const maximumAmountOfItemsToShow = computed(() => {
  return MAX_PICKER_ITEMS - 1
})

const remainingItemsCount = computed(() => {
  return props.step.options.length - maximumAmountOfItemsToShow.value
})

const options = computed(() => {
  if (shouldDisplayToggleButton.value && !displayAllItems.value) {
    return props.step.options.slice(0, maximumAmountOfItemsToShow.value)
  }

  return props.step.options
})

const trackingTags = computed(() => {
  const hasGoodDeal = props.step.options.some((option) => option.goodDeal)
  const hasSelectedGoodDeal = props.step.options.some(
    (option) => option.goodDeal && option.selected,
  )

  const hasStaffPick =
    props.step.id === 'grades' &&
    !props.step.options.some((option) => option.goodDeal)
  const hasSelectedStaffPick =
    hasStaffPick &&
    props.step.options.some(
      (option) => option.parameters?.grade?.value === 11 && option.selected,
    )

  return {
    // eslint-disable-next-line no-nested-ternary
    tagDisplayed: hasStaffPick
      ? 'staff_pick'
      : hasGoodDeal
        ? 'good_deal'
        : 'no_tag',
    tagSelected: hasSelectedStaffPick || hasSelectedGoodDeal,
  }
})

const trackingStep = computed(() => {
  const products = props.step.options.map((option) => ({
    product_id: option.productId,
    product_price:
      (typeof option.price === 'object'
        ? option.price?.amount
        : option.price) ?? null,
    product_variant: option.parameters?.grade?.value ?? null,
    product_special_offer: option.parameters?.specialOfferType?.value ?? null,
    product_available: option.available,
    product_option: option.label,
    campaign_id: props.pickers.selectedOffer?.campaignId,
    population_id: props.pickers.selectedOffer?.populationId,
  }))

  return {
    trackingProducts: products,
    trackingTags: trackingTags.value,
    trackingId: props.step.trackingId,
    trackingModel: props.product.model,
    trackingCategory: props.product.tracking.categoryName,
  }
})

const toggleLabel = computed(() => {
  return displayAllItems.value
    ? i18n(translations.showLess)
    : i18n(translations.showMore, {
        remaining: remainingItemsCount.value,
      })
})

const isSmallPicker = computed(() => {
  return (props.step.columns ?? 1) > 1
})

const isTitleWithoutEmphasis = computed(() => {
  return typeof props.step.title === 'string'
})

function stepOptionsClasses(step: Step) {
  if (step.columns && step.columns > 1) {
    return `grid grid-cols-1 sm:grid-cols-${step.columns} gap-x-12 gap-y-8`
  }
  if (step.columns === 1) {
    return 'grid grid-cols-1 gap-x-12 gap-y-8'
  }

  return tw`grid gap-y-8`
}
function toggleDisplayAllItems() {
  displayAllItems.value = !displayAllItems.value
}
function onOptionClick() {
  isGroupLoading.value = true
}
function onOptionChanged() {
  isGroupLoading.value = false
}
</script>
